import { $fetch } from '@block/utils';
import { CascaderProps } from '@src/types/components';
import { Pagination } from '@src/types/pagination';
import { hasValueObject } from '@src/utils';
import { prefix } from '@src/utils/prefix';
import { Cascader, notification } from '@ss/mtd-react';
import { CommonToProps } from '@ss/mtd-react/types/base';
import React, { useEffect, useMemo, useRef, useState } from 'react';

export interface AddressValueItem {
  code?: string;
  name?: string;
  children?: AddressValueItem[];
  leaf?: boolean;
  loading?: boolean;
}

export interface AddressValue {
  province?: AddressValueItem;
  city?: AddressValueItem;
  district?: AddressValueItem;
}

export interface AddressProps extends CommonToProps, Omit<CascaderProps, 'value' | 'onChange' | 'data'> {
  value?: AddressValue;
  onChange?: (v?: AddressValue) => void;
}

// 城市
export const getCity = async (params = {}, type = 'province') => {
  const res: Pagination<AddressValueItem> = await $fetch(`${prefix}/dataset/org/${type}`, params);
  return res?.pageList?.map((item) => ({ ...item, isLeaf: type === 'district' })) || [];
};

// export const getFilterCity = async (params) => {
//   const res: Pagination<AddressValueItem> = await $fetch('/component/api/dataset/authority/city/filter', params);
//   return res?.pageList || [];
// };

const dataConfig = {
  label: 'name',
  value: 'code',
};

export function Address({ value: outValue, separator = '/', onChange, ...props }: AddressProps) {
  const data = useRef<AddressValueItem[]>();
  const [, setUpdate] = useState(Symbol('update'));
  const [innerValue, setInnerValue] = useState(outValue);
  const [loading, setLoading] = useState(false);
  const ref = useRef<Cascader>();

  const { codes, names } = useMemo(() => {
    const { province, city, district } = innerValue || {};
    const codeList = [];
    const nameList = [];
    const v = province
      ? [province, city, district]?.filter((item) => {
          if (!item?.code) return false;
          codeList.push(item?.code);
          nameList.push(item?.name);
          return true;
        })
      : undefined;
    return { value: v, codes: codeList, names: nameList };
  }, [innerValue]);

  const update = () => {
    setUpdate(Symbol('update'));
  };

  const onFetchData: CascaderProps['loadData'] = async (address) => {
    const [province, city] = address || [];
    let options: AddressValueItem[] = data.current;
    if (!province) {
      options = await getCity();
    } else if (city) {
      const currentCity = options
        .find(({ code }) => province.code === code)
        ?.children?.find(({ code }) => city.code === code);
      if (currentCity) {
        currentCity.loading = true;
        update();
        try {
          currentCity.children = await getCity(
            {
              cityId: currentCity?.code,
            },
            'district',
          );
        } finally {
          currentCity.loading = false;
        }
      }
    } else if (province) {
      const currentProvince = options.find(({ code }) => code === province?.code);
      if (currentProvince) {
        currentProvince.loading = true;
        update();
        try {
          currentProvince.children = await getCity(
            {
              provinceId: province?.code,
            },
            'city',
          );
        } finally {
          currentProvince.loading = false;
        }
      }
    }
    data.current = options;
    update();
  };

  // const onFilter = async (filter: string) => {
  //   setLoading(true);
  //   try {
  //     if (!filter?.trim()) {
  //       data.current = [];
  //       await onFetchData([]);
  //     } else {
  //       const city = await getFilterCity({
  //         filter,
  //         componentCode: 'adminRegionFilter',
  //         dataType: 'district',
  //         highDataType: 'province',
  //         systemCode: 'ruzhi',
  //       });
  //       data.current = city;
  //     }
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleChange = (val: string[], selectedOptions: AddressValueItem[]) => {
    const [province, city, district] = selectedOptions || [];
    const v = hasValueObject({
      province,
      city,
      district,
    });
    setInnerValue(v);
    onChange?.(v);
  };

  const onVisibleChange: CascaderProps['onVisibleChange'] = async (v) => {
    if (!v) return;
    setLoading(true);
    let error: { message: string };
    try {
      await onFetchData([]);
    } catch (e) {
      error = e;
    } finally {
      setLoading(false);
    }
    if (outValue?.district && !error) {
      await onFetchData([outValue?.province]);
      await onFetchData([outValue?.province, outValue?.city]);
    }
    if (error) {
      notification.error({
        message: error?.message || '网络错误，请稍后重试',
      });
    }
  };

  useEffect(() => {
    if (outValue?.district?.code !== innerValue?.district?.code) {
      setInnerValue(outValue);
    }
  }, [outValue]);

  const formatter = () => {
    return names?.join(separator);
  };

  return (
    <Cascader
      remote
      // useLongChains
      // isKeywordHighlight
      changeOnSelect={false}
      className="c-address"
      ref={ref}
      value={codes}
      props={dataConfig}
      loading={loading}
      // loading={loading}
      data={data.current}
      loadData={onFetchData}
      onVisibleChange={onVisibleChange}
      // @ts-ignore
      // remoteMethod={onFilter}
      formatter={formatter}
      onChange={handleChange}
      {...props}
    />
  );
}
